// 1. Framework / library imports
import React, { useCallback, useMemo, useState } from "react";

// 2. Helpers & Hooks
import { useTranslate } from "helpers";
import { DropdownCacheKey, buildDropdownCacheKey, useDropdownCache } from "services/hooks/useDropdownCache";

// 3. API & Types
import {
  getVehicleManufacturersAPI,
} from "services/api/catalog/vehicles/manufacturers/api";
import {
  VehicleManufacturerRow,
} from "services/api/catalog/vehicles/manufacturers/types";
import { initialVehicleManufacturerDataWithPagination } from "./consts";
import { DataWithPagination } from "services/api";

// 4. UI Components
import { DropdownAutocomplete } from "../DropdownAutocomplete";
import { DropdownOption } from "../Dropdown/types";

// 5. Local Types
import { VehicleManufacturerDropdownProps } from "./types";

const FIVE_MINUTES = 5 * 60 * 1000;

const VehicleManufacturerDropdown: React.FC<VehicleManufacturerDropdownProps> = (
  props: VehicleManufacturerDropdownProps
) => {
  const {
    name,
    disabled,
    required,
    label,
    error,
    value,
    direction,
    width,
    height,
    placeholder,
    vehicleCategoryId,
    onChange,
  } = props;

  const { t } = useTranslate();

  const [searchValue, setSearchValue] = useState<string>("");

  const { data, isLoading } = useDropdownCache<DataWithPagination<VehicleManufacturerRow>>(
    buildDropdownCacheKey(DropdownCacheKey.VehicleManufacturers, vehicleCategoryId, searchValue),
    () => getVehicleManufacturersAPI(initialVehicleManufacturerDataWithPagination.pagination, searchValue, vehicleCategoryId),
    FIVE_MINUTES
  );

  const searchResults = data ?? initialVehicleManufacturerDataWithPagination;

  const handleSearchClick = useCallback(() => {
    setSearchValue("");
  }, []);

  const handleSearchChange = useCallback((name: string) => {
    setSearchValue(name);
  }, []);

  const vehicleManufacturerItems: DropdownOption[] = useMemo(() => {
    const hasPrimary = searchResults.data.some((i) => i.primary);
    return [...searchResults.data]
      .sort((a, b) => {
        if (a.primary !== b.primary) return a.primary ? -1 : 1;
        return a.displayOrder - b.displayOrder;
      })
      .map((i) => ({
        value: i.id,
        text: `${i.name}`,
        image: i.image,
        group: hasPrimary
          ? (i.primary ? t("admin.fleet.vehicle.manufacturer.group.primary") : t("admin.fleet.vehicle.manufacturer.group.other"))
          : undefined,
      }));
  }, [searchResults.data, t]);

  return (
    <>
      <DropdownAutocomplete
        label={label}
        name={name}
        disabled={disabled}
        required={required}
        placeholder={placeholder}
        value={value}
        direction={direction}
        width={width}
        height={height}
        options={vehicleManufacturerItems}
        onChange={onChange}
        onSearchChange={handleSearchChange}
        onSearchFocus={handleSearchClick}
        error={error}
        isLoading={isLoading}
      />
    </>
  );
};

export default React.memo(VehicleManufacturerDropdown);
